import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { createPostCheckoutSurveyOption } from '../api';
import { ConfirmationModal } from '../component/ConfirmationModal';
import { Dropdown } from '../component/Dropdown';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { countryOptions } from '../dropdownOptions';
import { CountryCode, Option, PostCheckoutSurveyOptionStatus } from '../pages/types';
import {
  STATUS_OPTIONS,
  generatePostCheckoutSurveyOptionValue,
  getCreatePostCheckoutSurveyOptionErrorMsg,
  isValidPostCheckoutSurveyOptionLabel,
} from '../utils/postCheckoutSurveyUtils';

const PPS_TOAST = { position: 'bottom-left' as const, hideProgressBar: true };

export const CreatePostCheckoutSurveyOptionPage = () => {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [country, setCountry] = useState<Option | undefined>(undefined);
  const [status, setStatus] = useState<Option | undefined>(undefined);
  const [marketingSource, setMarketingSource] = useState('');
  const [marketingBucket, setMarketingBucket] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    let value = '';
    try {
      if (!country) {
        throw new Error('Country must be selected');
      }
      const countryValue = country.value as CountryCode;
      value = generatePostCheckoutSurveyOptionValue(label, countryValue);
      await createPostCheckoutSurveyOption({
        label,
        value,
        status:
          (status?.value as PostCheckoutSurveyOptionStatus) ??
          PostCheckoutSurveyOptionStatus.ACTIVE,
        country: countryValue,
        marketing_source: marketingSource,
        marketing_bucket: marketingBucket,
      });
      toast.info(
        <div>
          <strong>Success!</strong>
          {/*  We will want to change this if we add a new question */}
          <p>
            A new option, "{label}," has been added to the post-purchase survey question "How did
            you hear about Spot &amp; Tango?"
          </p>
        </div>,
        PPS_TOAST
      );
      navigate('/post-checkout-survey-options');
    } catch (e) {
      toast.error(getCreatePostCheckoutSurveyOptionErrorMsg(e, value), PPS_TOAST);
    } finally {
      setIsSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="relative h-[calc(100vh-77px)] bg-slate50 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Create new option</h1>
        <div className="border rounded-lg p-6 space-y-4">
          <div>
            <div className="font-semibold mb-1">
              Name<span className="text-red-500">*</span>
            </div>
            <TextInput placeholder="e.g., Facebook" value={label} setTextChange={setLabel} />
          </div>
          <div>
            <div className="font-semibold mb-1">Source</div>
            <TextInput
              placeholder="e.g., facebook"
              value={marketingSource}
              setTextChange={setMarketingSource}
            />
          </div>
          <div>
            <div className="font-semibold mb-1">Bucket</div>
            <TextInput
              placeholder="e.g., facebook"
              value={marketingBucket}
              setTextChange={setMarketingBucket}
            />
          </div>
          <div>
            <div className="font-semibold mb-1">
              Country<span className="text-red-500">*</span>
            </div>
            <Dropdown
              options={countryOptions}
              selectedOption={country}
              setSelectedOption={setCountry}
              defaultText="Select a country"
              noDefaultSelection
            />
          </div>
          <div>
            <div className="font-semibold mb-1">
              Status<span className="text-red-500">*</span>
            </div>
            <Dropdown
              options={STATUS_OPTIONS}
              selectedOption={status}
              setSelectedOption={setStatus}
              defaultText="Select a status"
              noDefaultSelection
            />
          </div>
        </div>
        <div className="mt-6 flex justify-center">
          <Button
            label="Create option"
            variant="primary"
            isDisabled={
              !status || !country || isSubmitting || !isValidPostCheckoutSurveyOptionLabel(label)
            }
            onClick={() => setShowConfirm(true)}
          />
        </div>

        {showConfirm && (
          <ConfirmationModal
            title="Are you sure you want to proceed with creating this new option?"
            message="Please make sure the details are correct before confirming."
            confirmLabel="Confirm"
            onConfirm={handleSubmit}
            onCancel={() => setShowConfirm(false)}
            isProcessLoading={isSubmitting}
          />
        )}
      </div>
    </div>
  );
};
